import {useCallback, useRef} from 'react';

/**
 * Custom hook to debounce an async function
 * @param callback - The async function to debounce
 * @param delay - The delay in milliseconds
 * @returns The debounced function
 */
function useDebouncedCallback<T extends (...args: any[]) => Promise<any>>(
  callback: T,
  delay: number
): T {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedCallback = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      return new Promise<ReturnType<T>>((resolve, reject) => {
        timeoutRef.current = setTimeout(async () => {
          try {
            const result = await callback(...args);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        }, delay);
      });
    },
    [callback, delay]
  ) as T;

  return debouncedCallback;
}

export default useDebouncedCallback;
